import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { DashboardDef, WidgetDef } from '@project/contracts'
import { Button, Empty, Icon, Lookup, Money, Panel } from '../design/index.js'
import { useApi, type WidgetData } from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths, type Query } from '../app/router.js'
import { ErrorBlock, Loading } from './feedback.js'
import { DATE_PRESETS, answersRequired, defaultFilterValues } from './filters.js'

const CAT = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)', 'var(--chart-6)']

const useDashboardDefs = () => {
  const api = useApi()
  return useQuery({ queryKey: ['dashboardDefs'], queryFn: () => api.dashboardMenu(), staleTime: 10 * 60_000 })
}

export const DashboardsHub = ({ companyId }: { companyId: string }) => {
  const defs = useDashboardDefs()
  if (defs.isLoading) return <div className="page"><Loading /></div>
  if (defs.error) return <div className="page"><ErrorBlock error={defs.error} /></div>
  return (
    <div className="page">
      <h1 className="page-title">Dashboards</h1>
      <p className="page-subtitle">The shape of the business, rather than a list of documents.</p>
      <Panel title="Dashboards" aside={`${(defs.data ?? []).length}`}>
        <div className="tile-grid">
          {(defs.data ?? []).map((d) => (
            <div className="tile" key={d.key}>
              <div className="tile__body">
                <Link to={paths.dashboard(companyId, d.route)} className="tile__title">{d.label}</Link>
                <p className="tile__desc">{d.blurb}</p>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  )
}

/** Numbers, drawn the way the widget says. No widget knows its own subject. */
/**
 * Where a widget row leads (docs/03 — `Show details`).
 *
 * Each drillable widget names the filter its rows set, and the row carries the
 * id to set it to. A project bar opens that project's financials; a supplier
 * bar opens what has been bought from them.
 */
const REPORT_FOR: Readonly<Record<string, string>> = {
  projectId: 'project_management_dashboard',
  supplierId: 'purchase_price_history',
  jobTypeId: 'project_management_dashboard',
}

const Widget = ({
  def, data, companyId, filters,
}: {
  def: WidgetDef
  data: WidgetData | undefined
  companyId: string
  filters: Readonly<Record<string, string>>
}) => {
  const openRow = (id: string | undefined): void => {
    if (!id || !def.drillTo) return
    const report = REPORT_FOR[def.drillTo]
    if (!report) return
    // Carry the dashboard's own period across, so the report opens on the same
    // window the figure was read from.
    navigate(paths.report(companyId, report, {
      ...(filters['from'] ? { from: filters['from'] } : {}),
      ...(filters['to'] ? { to: filters['to'] } : {}),
      [def.drillTo]: id,
    }))
  }

  const body = (): JSX.Element => {
    if (!data || data.kind === 'empty') {
      return <p className="muted">{data?.note ?? 'Nothing to show for these filters.'}</p>
    }
    if (data.kind === 'kpi') {
      return (
        <div style={{ display: 'flex', gap: 44, flexWrap: 'wrap' }}>
          {data.items.map((i) => (
            <div key={i.label}>
              <div className="widget__label">{i.label}</div>
              <div className="widget__figure num">
                {def.format === 'money' ? <Money value={i.value} size={27} /> : i.value}
              </div>
            </div>
          ))}
        </div>
      )
    }
    if (data.kind === 'split') {
      return (
        <>
          <div className="widget__label">Total</div>
          <div className="widget__figure num" style={{ marginBottom: 18 }}><Money value={data.total} size={27} /></div>
          <div className="splitbar">
            {data.parts.map((p, i) => (
              <span key={p.label} style={{ width: `${p.pct}%`, background: CAT[i % CAT.length] }} />
            ))}
          </div>
          <div className="splitlist">
            {data.parts.map((p, i) => (
              <div className="splitlist__row" key={p.label}>
                <span className="splitlist__swatch num" style={{ background: CAT[i % CAT.length] }}>{i + 1}</span>
                {def.drillTo && p.id ? (
                  <button type="button" className="splitlist__name linkish" onClick={() => openRow(p.id)}>
                    {p.label}
                  </button>
                ) : (
                  <span className="splitlist__name">{p.label}</span>
                )}
                <span className="num splitlist__pct">{p.pct}%</span>
                <span className="num splitlist__val"><Money value={p.value} /></span>
              </div>
            ))}
          </div>
        </>
      )
    }
    if (data.kind === 'rank') {
      const max = Math.max(...data.items.map((i) => Math.abs(Number(i.value))), 1)
      return (
        <div>
          {data.items.map((i) => (
            <div key={i.label} className="rankrow">
              <div className="rankrow__head">
                {def.drillTo && i.id ? (
                  <button type="button" className="rankrow__name linkish" onClick={() => openRow(i.id)}>
                    {i.label}
                  </button>
                ) : (
                  <span className="rankrow__name">{i.label}</span>
                )}
                <span className="num"><Money value={i.value} /></span>
              </div>
              <div className="rankrow__track">
                <span style={{ width: `${(Math.abs(Number(i.value)) / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )
    }
    if (data.kind === 'gauge') {
      return (
        <div>
          {data.items.map((i) => (
            <div key={i.label} className="rankrow">
              <div className="rankrow__head">
                <span className="rankrow__name">{i.label}</span>
                <span className="num"><Money value={i.value} /> · {i.pct}%</span>
              </div>
              <div className="rankrow__track">
                <span style={{ width: `${Math.min(Number(i.pct), 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )
    }
    // series
    const all = data.series.flatMap((s) => s.points.map(Number))
    const max = Math.max(...all, 1)
    return (
      <>
        <div style={{ display: 'flex', gap: 22, marginBottom: 18 }}>
          {data.series.map((s, i) => (
            <span key={s.name} className="legend">
              <span className="legend__swatch" style={{ background: CAT[i % CAT.length] }} />{s.name}
            </span>
          ))}
        </div>
        <div className="chart">
          {data.labels.map((l, x) => (
            <div className="chart__group" key={l}>
              <div className="chart__bars">
                {data.series.map((s, i) => (
                  <span key={s.name} title={`${s.name} ${l}`}
                    style={{ height: `${(Number(s.points[x] ?? 0) / max) * 100}%`, background: CAT[i % CAT.length] }} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="chart__axis">{data.labels.map((l) => <span key={l}>{l}</span>)}</div>
      </>
    )
  }

  return (
    <div style={{ gridColumn: def.span === 2 ? 'span 2' : undefined }}>
      <Panel title={def.title} aside={def.subtitle}>{body()}</Panel>
    </div>
  )
}

export const DashboardPage = ({ companyId, dashboardRoute, query }: {
  companyId: string; dashboardRoute: string; query: Query
}) => {
  const api = useApi()
  const defs = useDashboardDefs()
  const def: DashboardDef | undefined = defs.data?.find((d) => d.route === dashboardRoute)

  const [draft, setDraft] = useState<Record<string, string>>(query as Record<string, string>)
  const [applied, setApplied] = useState<Record<string, string> | null>(
    Object.keys(query).length > 0 ? (query as Record<string, string>) : null,
  )

  const defaults = useMemo(() => defaultFilterValues(def?.filters ?? []), [def])
  const seeded = useRef(false)
  useEffect(() => {
    // Runs once the definition arrives: a page opened without a query in the
    // address bar starts on the default period, and shows figures straight away
    // when that is all it was waiting for.
    if (seeded.current || !def || Object.keys(query).length > 0) return
    seeded.current = true
    if (Object.keys(defaults).length === 0) return
    setDraft((current) => ({ ...defaults, ...current }))
    if (answersRequired(def.filters, defaults)) setApplied(defaults)
  }, [def, defaults, query])


  const params = useMemo(() => {
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(applied ?? {})) if (v) out[k] = v
    return out
  }, [applied])

  const run = useQuery({
    queryKey: ['dashboard', api.companyId, def?.key, params],
    queryFn: () => api.runDashboard(def?.key ?? '', params),
    enabled: Boolean(def?.key) && (applied !== null || (def?.filters.length ?? 0) === 0),
  })

  if (defs.isLoading) return <div className="page"><Loading /></div>
  if (!def) return <div className="page"><ErrorBlock error={new Error(`No dashboard at '${dashboardRoute}'`)} /></div>

  const needsFilters = def.filters.some((f) => f.required)

  return (
    <div className="page">
      <Link to={paths.hub(companyId, 'dashboards')} className="crumb"><Icon name="left" size={15} /> Dashboards</Link>
      <h1 className="page-title">{def.label}</h1>
      <p className="page-subtitle">{def.blurb}</p>

      {def.filters.length > 0 ? (
        <Panel title="Filters">
          <div className="field-grid">
            {def.filters.map((f) => (
              <div className="field" key={f.key}>
                <label className="field__label">
                  {f.label}{f.required ? <span className="field__req">Required</span> : null}
                </label>
                {f.control === 'dateRange' ? (
                  <>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input className="control" type="date" value={draft['from'] ?? ''}
                      onChange={(e) => setDraft({ ...draft, from: e.currentTarget.value })} />
                    <input className="control" type="date" value={draft['to'] ?? ''}
                      onChange={(e) => setDraft({ ...draft, to: e.currentTarget.value })} />
                  </div>
                  <div className="presets">
                    {DATE_PRESETS.map((preset) => (
                      <button
                        key={preset.key}
                        type="button"
                        className="preset"
                        onClick={() => setDraft({ ...draft, ...preset.range() })}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  </>
                ) : f.control === 'lookup' && f.lookup ? (
                  <Lookup entity={f.lookup.entity} value={draft[f.key] ?? ''}
                    onChange={(v) => setDraft({ ...draft, [f.key]: v })} placeholder="Start typing to search" />
                ) : (
                  <input className="control" value={draft[f.key] ?? ''}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.currentTarget.value })} />
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <Button kind="primary" onClick={() => {
              setApplied(draft)
              navigate(paths.dashboard(companyId, dashboardRoute, draft))
            }}>Apply</Button>
            <Button kind="ghost" onClick={() => { setDraft({}); setApplied(null) }}>Clear</Button>
          </div>
        </Panel>
      ) : null}

      <div style={{ marginTop: 24 }}>
        {applied === null && needsFilters ? (
          <Empty title="Apply the filters"
            body="Dashboards read across every project, so nothing loads until you say what period you want." />
        ) : run.isLoading ? <Loading />
          : run.error ? <ErrorBlock error={run.error} />
          : (
            <div className="widget-grid">
              {def.widgets.map((w) => (
                <Widget
                  key={w.key}
                  def={w}
                  data={run.data?.widgets[w.key]}
                  companyId={companyId}
                  filters={params}
                />
              ))}
            </div>
          )}
      </div>
    </div>
  )
}
